import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryKeys } from '@/hooks/queries';
import type { User } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export const useInquiryCount = (user: User | null) => {
  const queryClient = useQueryClient();

  const isEligible = Boolean(user) && (user!.is_admin === true || user!.is_partner === true);

  const { data: newInquiryCount = 0 } = useQuery({
    queryKey: queryKeys.inquiries.count.new(user?.id ?? '', user?.assigned_provider_ids ?? []),
    queryFn: async () => {
      if (!isEligible || !user) return 0;

      let query = supabase
        .from('inquiries')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'new');

      // If partner, filter by assigned providers
      if (
        user.is_partner &&
        !user.is_admin &&
        Array.isArray(user.assigned_provider_ids) &&
        user.assigned_provider_ids.length > 0
      ) {
        query = query.in('clinic_id', user.assigned_provider_ids);
      }

      const { count, error } = await query;

      if (error) {
        return 0;
      }

      return count ?? 0;
    },
    enabled: !!isEligible,
    staleTime: 1000 * 60 * 1, // 1 minute
    refetchInterval: 1000 * 60 * 2, // Refetch every 2 minutes
  });

  useEffect(() => {
    if (!isEligible || !user) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel('inquiry-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'inquiries',
          },
          () => {
            queryClient
              .invalidateQueries({
                queryKey: queryKeys.inquiries.count.new(user.id, user.assigned_provider_ids ?? []),
              })
              .catch(() => {
                // Query invalidation error handling
              });
          }
        )
        .subscribe();
    } catch (error) {
      // Silently handle WebSocket connection errors during development/hot reloading
      console.warn('Failed to establish realtime connection:', error);
    }

    return () => {
      if (channel) {
        try {
          void supabase.removeChannel(channel);
        } catch {
          // Ignore cleanup errors during component unmount
        }
      }
    };
  }, [user, isEligible, queryClient]);

  return newInquiryCount;
};
